import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Repeat, ArrowLeftRight } from "lucide-react";
import LegsEditor from "@/components/LegsEditor";
import { reverseLegs } from "@/lib/routeSync";

const total = (legs) => (legs || []).reduce((s, l) => s + (Number(l.distance) || 0), 0);

const TextField = ({ label, value, onChange, tid, placeholder, mono }) => (
  <div>
    <Label className="text-[11px] uppercase tracking-wider text-slate-400">{label}</Label>
    <Input value={value} onChange={(e) => onChange(e.target.value)} data-testid={tid} placeholder={placeholder}
      className={`mt-1 border-slate-700 bg-slate-900/60 text-slate-100 ${mono ? "font-mono-data" : ""}`} />
  </div>
);

const LegsSection = ({ title, legs, children }) => (
  <div className="glass rounded-2xl p-6">
    <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
      <h2 className="font-display text-base font-semibold uppercase tracking-wide text-cyan-400">{title}</h2>
      <span className="font-mono-data text-sm text-slate-300">{total(legs)} NM total</span>
    </div>
    {children}
  </div>
);

export default function RouteFields({ form, setForm }) {
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setList = (key) => (fn) => setForm((f) => ({ ...f, [key]: fn(f[key] || []) }));
  const toggleRoundTrip = (on) =>
    setForm((f) => ({ ...f, roundTrip: on, returnLegs: on && !(f.returnLegs || []).length ? reverseLegs(f.legs, f.departure) : f.returnLegs || [] }));

  return (
    <>
      <div className="glass rounded-2xl p-6">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <TextField label="Nom de la route" value={form.name} onChange={(v) => set("name", v)} tid="route-name-input" placeholder="Toussus → Chartres" />
          <TextField label="Départ (OACI)" value={form.departure} onChange={(v) => set("departure", v.toUpperCase())} tid="route-departure-input" placeholder="LFPN" mono />
          <TextField label="Arrivée (OACI)" value={form.arrival} onChange={(v) => set("arrival", v.toUpperCase())} tid="route-arrival-input" placeholder="LFOR" mono />
        </div>
        <label className="mt-4 flex w-fit cursor-pointer items-center gap-3 rounded-lg border border-slate-700 bg-slate-900/40 px-3 py-2">
          <Switch checked={!!form.roundTrip} onCheckedChange={toggleRoundTrip} data-testid="route-roundtrip-toggle" />
          <Repeat className="h-4 w-4 text-cyan-400" />
          <span className="text-sm text-slate-200">Aller / Retour</span>
          <span className="text-xs text-slate-500">(branches retour distinctes)</span>
        </label>
      </div>

      <LegsSection title={form.roundTrip ? "Aller — branches / points tournants" : "Branches / Points tournants"} legs={form.legs}>
        <LegsEditor legs={form.legs} setLegs={setList("legs")} />
      </LegsSection>

      {form.roundTrip && (
        <LegsSection title={`Retour — ${form.arrival || "?"} → ${form.departure || "?"}`} legs={form.returnLegs}>
          <button onClick={() => set("returnLegs", reverseLegs(form.legs, form.departure))} data-testid="route-generate-return"
            className="mb-4 flex items-center gap-1.5 rounded-md border border-slate-700 px-3 py-1.5 text-xs text-cyan-300 hover:bg-cyan-500/10">
            <ArrowLeftRight className="h-3.5 w-3.5" /> Générer le retour depuis l'aller (remplace les branches retour)
          </button>
          <LegsEditor legs={form.returnLegs || []} setLegs={setList("returnLegs")} p="ret-" />
        </LegsSection>
      )}
    </>
  );
}
